import React from "react";
import { ListJobsService } from "@application/useCases";
import { StatusEnumSchema } from "@domain/schemas/BI";
import { JobListItemState } from "@presentation/common/types/extensions/JobListItemState";
import ShowLogsButton from "./ShowLogsButton";
import { useTranslation } from "react-i18next";

type JobStatusLogsCellProperties = {
  job: Pick<JobListItemState, "id" | "status" | "currentStepId" | "currentStepIsParallel">;
  listJobsService: ListJobsService;
  openLogs: (jobId: string, jobPipelineStepId: string, batch: { id: string; status?: StatusEnumSchema | null }) => Promise<void>;
};

export const JobStatusLogsCell: React.FC<JobStatusLogsCellProperties> = ({ job, listJobsService, openLogs }) => {
  const { t: loc } = useTranslation();
  const currentStepId = typeof job.currentStepId === "string" ? job.currentStepId : job.currentStepId?.toString();
  const canShowCurrentStepLogs = job.status === StatusEnumSchema.Running && job.currentStepIsParallel === false && Boolean(currentStepId);

  if (!canShowCurrentStepLogs || !currentStepId) {
    return null;
  }

  const openCurrentStepLogs = async () => {
    try {
      const batchResponse = await listJobsService.getJobStepBatchListAsync(job.id, currentStepId);
      const firstBatch = batchResponse.list?.[0];

      if (!firstBatch) {
        alert(loc("noBatchFoundForCurrentStepLogs"));
        return;
      }

      await openLogs(job.id, currentStepId, { id: firstBatch.id, status: firstBatch.status });
    } catch (error) {
      // Without this the rejection is swallowed by the `void` call sites and the user gets no feedback at all.
      console.error(`Error opening current step logs for job ${job.id}:`, error);
      alert(loc("currentStepLogsLoadError"));
    }
  };

  return (
    <ShowLogsButton
      onMouseUp={(event) => {
        event.stopPropagation();
        event.preventDefault();
        void openCurrentStepLogs();
      }}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        if (event.detail === 0) {
          void openCurrentStepLogs();
        }
      }}
      aria-label={loc("showCurrentStepLogs")}
    />
  );
};
